/**
 * GA4 event tracking (WEB-73 to WEB-76)
 *
 * Sends GA4 events directly via gtag.js (no GTM). gtag.js is already loaded on
 * every page by the existing Google Ads tag in layout/theme.liquid; this file
 * reuses that loader and targets the GA4 property via window.GA4_MEASUREMENT_ID
 * (set from the "GA4 Measurement ID" theme setting). Each event is scoped with
 * send_to so it reaches GA4 only, not the Google Ads account.
 *
 * If no GA4 Measurement ID is configured, all tracking is a no-op.
 *
 * Tracked:
 *   WEB-73 Slider:    view_promotion, select_promotion, slider_navigate
 *   WEB-74 Promotion: view_promotion, select_promotion
 *   WEB-75 Grid:      view_item_list, select_item, select_content
 *   WEB-76 Mobile:    menu_open, menu_navigate, menu_submenu_toggle
 *   Promo funnel:     promo_add_to_cart (see GA4-PROMO-PIXEL.js for
 *                     promo_purchase, sent from a Shopify custom web pixel)
 *
 * Markup contract (added in Liquid):
 *   Promotions  -> [data-ga4-promotion] with data-promotion-id / -name /
 *                  data-creative-name / data-creative-slot / data-location-id
 *   Item lists  -> [data-ga4-list] with data-list-id / data-list-name
 *   Product card-> .grid-product[data-product-id] with data-product-title /
 *                  data-product-price
 *   Category    -> .collection-item inside [data-ga4-category]
 */
(function () {
  'use strict';

  // ------------------------------------------------------------------
  // THEME ADAPTER
  // Everything theme-specific lives here. The rest of this file only deals
  // with the stable data-ga4-* markup contract and GA4 event logic, so
  // porting to another store/theme means overriding these selectors —
  // either by editing the defaults below or by defining window.GA4_SELECTORS
  // BEFORE this script loads (e.g. in theme.liquid).
  // ------------------------------------------------------------------
  var DEFAULT_SELECTORS = {
    // Clickable elements inside promotions (fire select_promotion). Elements
    // matching these should sit inside a [data-ga4-promotion] marker.
    promoClick: [
      '.hero__slide-link',
      '.promo-grid__slide-link',
      'a.promo-grid__container',
      '.promo-grid__container a',
      '.type-banner__link',
      '.countdown__button',
      '.footer-promotion a',
      '.findify-promo-card-cta-button',
      '[data-section-type="background-image"] a'
    ].join(','),
    // Fallbacks used to derive a promotion name from the DOM when the
    // data-promotion-name attribute is empty (tried in order).
    promoTitleFallback: ['.promo-grid__title', '.h3'],
    // Product card link + card element inside [data-ga4-list] grids.
    itemLink: '.grid-item__link',
    itemCard: '.grid-product[data-product-id]',
    // Fallbacks when the card has no data-product-id/-title/-price attributes:
    // itemOptionsAttr names an attribute holding a JSON blob with id/price
    // (e.g. T4S themes use data-product-options); title/price selectors are
    // queried inside the card. Empty string disables a fallback.
    itemOptionsAttr: '',
    itemTitleFallback: '',
    itemPriceFallback: '',
    // Category / collection tile (inside [data-ga4-category] when marked up).
    categoryTile: '.collection-item',
    categoryTitle: '.collection-item__title',
    // Mobile navigation.
    mobileNavRoot: '#MobileNav',
    mobileNavLink: '#MobileNav a.slide-nav__link',
    mobileNavLinkLabel: 'span',
    mobileSubmenuToggle: '.js-toggle-submenu',
    mobileSubmenuLabel: '.slide-nav__link > span',
    mobileMenuOpenEvent: 'MobileNav:open', // custom DOM event the theme fires
    // Alternative to mobileMenuOpenEvent for themes that open the drawer via
    // framework-internal events (e.g. jQuery-only): a click selector for the
    // element(s) that open the mobile menu (hamburger button etc.).
    mobileMenuOpenTrigger: '',
    // Menu tier detection. menuTierAttr reads an attribute off the closest
    // ancestor that has it; menuTierClassPattern (regex string with one
    // capture group, e.g. 't4s-item-level-(\\d+)') matches ancestor class
    // names instead; menuTierOffset is added to the captured number (for
    // themes whose levels are 0-based).
    menuTierAttr: 'data-level',
    menuTierClassPattern: '',
    menuTierOffset: 0,
    // Desktop header navigation.
    desktopNavLink: 'a.site-nav__link, a.site-nav__dropdown-link',
    desktopNavDropdownClass: 'site-nav__dropdown-link',
    desktopNavTier2Classes: ['site-nav__dropdown-link--top-level', 'site-nav__dropdown-link--second-level'],
    // Ancestor-based desktop tier detection: array of { selector, tier }
    // checked in order with closest(); first match wins. When empty, the
    // class-based desktopNavDropdownClass/Tier2Classes logic above is used.
    desktopNavTierContainers: [],
    // Slider controls (fire slider_navigate) and the slider section wrapper.
    sliderControl: '.flickity-prev-next-button, .flickity-page-dots .dot',
    sliderSection: '[data-section-type="slideshow-section"]',
    sliderDotClass: 'dot',
    sliderPrevClass: 'previous',
    // When true, every event is ALSO pushed to window.dataLayer (same event
    // name, params flat + copied under "ecommerce"), so GTM custom-event
    // triggers keep working on stores that route tracking through GTM.
    pushToDataLayer: false
  };

  var SELECTORS = (function () {
    var merged = {};
    var overrides = window.GA4_SELECTORS || {};
    for (var k in DEFAULT_SELECTORS) {
      if (Object.prototype.hasOwnProperty.call(DEFAULT_SELECTORS, k)) {
        merged[k] = overrides[k] !== undefined ? overrides[k] : DEFAULT_SELECTORS[k];
      }
    }
    return merged;
  })();

  var PROMO_CLICK_SELECTOR = SELECTORS.promoClick;

  var observed = typeof WeakSet === 'function' ? new WeakSet() : null;

  // --- Click-attribution contexts (click -> add_to_cart funnels) ---
  // The last clicked promotion / category tile / product list is kept in
  // sessionStorage so a later add-to-cart can be attributed to it. Each
  // context is independent, valid 30 minutes, last-click wins.
  var CTX_TTL_MS = 30 * 60 * 1000;
  var PROMO_CTX_KEY = 'ga4_promo_ctx';
  var CATEGORY_CTX_KEY = 'ga4_category_ctx';
  var LIST_CTX_KEY = 'ga4_list_ctx';
  var MENU_CTX_KEY = 'ga4_menu_ctx';

  function storeCtx(key, data) {
    try {
      data.ts = Date.now();
      window.sessionStorage.setItem(key, JSON.stringify(data));
    } catch (err) {
      /* sessionStorage unavailable (private mode etc.) - attribution skipped */
    }
  }

  function getCtx(key, requiredField) {
    try {
      var raw = window.sessionStorage.getItem(key);
      if (!raw) return null;
      var ctx = JSON.parse(raw);
      if (!ctx || !ctx[requiredField]) return null;
      if (Date.now() - (ctx.ts || 0) > CTX_TTL_MS) {
        window.sessionStorage.removeItem(key);
        return null;
      }
      return ctx;
    } catch (err) {
      return null;
    }
  }

  function storePromoContext(promo) {
    if (!promo || !promo.promotion_id) return;
    storeCtx(PROMO_CTX_KEY, {
      promotion_id: promo.promotion_id,
      promotion_name: promo.promotion_name || '',
      creative_name: promo.creative_name || '',
      location_id: promo.location_id || ''
    });
  }

  function getPromoContext() {
    return getCtx(PROMO_CTX_KEY, 'promotion_id');
  }

  function storeCategoryContext(id, name) {
    if (!name && !id) return;
    storeCtx(CATEGORY_CTX_KEY, { category_id: id || '', category_name: name || '' });
  }

  function storeListContext(listId, listName, productId, productTitle) {
    if (!listName && !listId) return;
    storeCtx(LIST_CTX_KEY, {
      list_id: listId || '',
      list_name: listName || '',
      product_id: productId || '',
      product_title: productTitle || ''
    });
  }

  function storeMenuContext(menuItem, menuTier, linkUrl) {
    if (!menuItem) return;
    storeCtx(MENU_CTX_KEY, {
      menu_item: menuItem,
      menu_tier: menuTier || '',
      link_url: linkUrl || ''
    });
  }

  // Pull the variant id out of a /cart/add request body (form-encoded,
  // FormData or JSON, depending on which part of the theme fired it).
  function variantIdFromBody(body) {
    try {
      if (!body) return '';
      if (typeof body === 'string') {
        var m = body.match(/(?:^|&)id=([^&]+)/);
        if (m) return decodeURIComponent(m[1]);
        var json = JSON.parse(body);
        if (json && json.id) return String(json.id);
        if (json && json.items && json.items[0] && json.items[0].id) {
          return String(json.items[0].id);
        }
      } else if (typeof FormData === 'function' && body instanceof FormData && body.get) {
        return String(body.get('id') || '');
      } else if (typeof URLSearchParams === 'function' && body instanceof URLSearchParams) {
        return String(body.get('id') || '');
      }
    } catch (err) {
      /* unparseable body - variant id simply omitted */
    }
    return '';
  }

  // Persist the promo context onto the Shopify cart so checkout (where theme
  // JS cannot run) can attribute the purchase. Underscore-prefixed attribute
  // names stay out of customer-facing notes but are visible on the order in
  // Shopify Admin and readable by the custom web pixel (GA4-PROMO-PIXEL.js).
  function setPromoCartAttributes(ctx, fetchFn) {
    try {
      fetchFn('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            _ga4_promo_id: ctx.promotion_id,
            _ga4_promo_name: ctx.promotion_name || '',
            _ga4_promo_creative: ctx.creative_name || '',
            _ga4_promo_location: ctx.location_id || ''
          }
        })
      });
    } catch (err) {
      /* attribute write is best-effort */
    }
  }

  // The theme adds to cart via AJAX (theme.AjaxProduct -> fetch('/cart/add')),
  // so a fetch wrapper catches every add-to-cart, including quick-add. Each
  // fresh click context (promotion / category / list) fires its own
  // attributed add-to-cart event; the promo one additionally tags the cart.
  function watchCartAdds() {
    if (typeof window.fetch !== 'function') return;
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (url.indexOf('/cart/add') !== -1) {
          var variantId = variantIdFromBody(init && init.body);

          var promoCtx = getPromoContext();
          if (promoCtx) {
            var params = {
              promotion_id: promoCtx.promotion_id,
              promotion_name: promoCtx.promotion_name || ''
            };
            if (promoCtx.creative_name) params.creative_name = promoCtx.creative_name;
            if (promoCtx.location_id) params.location_id = promoCtx.location_id;
            if (variantId) params.variant_id = variantId;
            track('promo_add_to_cart', params);
            setPromoCartAttributes(promoCtx, origFetch);
          }

          var catCtx = getCtx(CATEGORY_CTX_KEY, 'category_name');
          if (catCtx) {
            var catParams = { category_name: catCtx.category_name };
            if (catCtx.category_id) catParams.category_id = catCtx.category_id;
            if (variantId) catParams.variant_id = variantId;
            track('category_add_to_cart', catParams);
          }

          var listCtx = getCtx(LIST_CTX_KEY, 'list_name');
          if (listCtx) {
            var listParams = { collection_title: listCtx.list_name };
            if (listCtx.list_id) listParams.collection_id = listCtx.list_id;
            if (listCtx.product_title) listParams.product_title = listCtx.product_title;
            if (listCtx.product_id) listParams.product_id = listCtx.product_id;
            if (variantId) listParams.variant_id = variantId;
            track('list_add_to_cart', listParams);
          }

          var menuCtx = getCtx(MENU_CTX_KEY, 'menu_item');
          if (menuCtx) {
            var menuParams = { menu_item: menuCtx.menu_item };
            if (menuCtx.menu_tier) menuParams.menu_tier = menuCtx.menu_tier;
            if (menuCtx.link_url) menuParams.link_url = menuCtx.link_url;
            if (variantId) menuParams.variant_id = variantId;
            track('menu_add_to_cart', menuParams);
          }
        }
      } catch (err) {
        /* tracking must never break the store's cart requests */
      }
      return origFetch.apply(this, arguments);
    };
  }

  // Funnel events carry a numeric "1" parameter so they can be registered as
  // event-scoped custom metrics in GA4. Calculated metrics (Admin > Custom
  // definitions) can then derive rates (CTR, ATC rate) that work in
  // Explorations, where "Event count where event = X" can't be used in math.
  var METRIC_PARAMS = {
    view_promotion: 'promo_views',
    select_promotion: 'promo_clicks',
    promo_add_to_cart: 'promo_atcs',
    select_content: 'category_clicks',
    category_add_to_cart: 'category_atcs',
    menu_navigate: 'menu_clicks',
    menu_add_to_cart: 'menu_atcs',
    select_item: 'list_clicks',
    featured_collection_select: 'list_clicks',
    featured_product_select: 'list_clicks',
    list_add_to_cart: 'list_atcs'
  };

  // Send a GA4 event via gtag, scoped to the GA4 property only. Optionally
  // mirrors the event onto window.dataLayer for GTM-based stores.
  function track(eventName, params) {
    var data = {};
    if (params) {
      for (var key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key)) {
          data[key] = params[key];
        }
      }
    }
    var metricParam = METRIC_PARAMS[eventName];
    if (metricParam && data[metricParam] === undefined) data[metricParam] = 1;

    if (SELECTORS.pushToDataLayer) {
      try {
        window.dataLayer = window.dataLayer || [];
        var dl = { event: eventName, ecommerce: {} };
        for (var dlKey in data) {
          if (Object.prototype.hasOwnProperty.call(data, dlKey)) {
            dl[dlKey] = data[dlKey];
            dl.ecommerce[dlKey] = data[dlKey];
          }
        }
        window.dataLayer.push(dl);
      } catch (err) {
        /* dataLayer mirroring is best-effort */
      }
    }

    if (typeof window.gtag !== 'function') return;
    var id = window.GA4_MEASUREMENT_ID;
    if (!id) return; // GA4 not configured via gtag (avoid sending to Ads).
    data.send_to = id;
    window.gtag('event', eventName, data);
  }

  // Resolve the nearest Element for a possibly non-element event target (e.g. SVG/text).
  function closest(node, selector) {
    var el = node;
    while (el && el.nodeType !== 1) {
      el = el.parentNode;
    }
    return el && el.closest ? el.closest(selector) : null;
  }

  function text(el) {
    return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : '';
  }

  function attr(el, name) {
    return el && el.getAttribute ? el.getAttribute(name) || '' : '';
  }

  function promotionData(marker, fallbackEl) {
    var source = marker || fallbackEl;
    var id = attr(marker, 'data-promotion-id');
    var name = attr(marker, 'data-promotion-name');
    var creative = attr(marker, 'data-creative-name');
    var slot = attr(marker, 'data-creative-slot');
    var location = attr(marker, 'data-location-id');

    if (!name && source && source.querySelector) {
      var img = source.querySelector('img[alt]');
      var titleEl = null;
      for (var t = 0; t < SELECTORS.promoTitleFallback.length && !titleEl; t++) {
        titleEl = source.querySelector(SELECTORS.promoTitleFallback[t]);
      }
      name = (img && img.getAttribute('alt')) || text(titleEl) || attr(fallbackEl, 'aria-label');
    }

    var promo = {};
    if (id) promo.promotion_id = id;
    if (name) promo.promotion_name = name;
    if (creative) promo.creative_name = creative;
    if (slot) promo.creative_slot = slot;
    if (location) promo.location_id = location;
    return promo;
  }

  function listContext(el) {
    var listEl = closest(el, '[data-ga4-list]');
    return {
      el: listEl,
      id: attr(listEl, 'data-list-id'),
      name: attr(listEl, 'data-list-name')
    };
  }

  // Themes without per-card data-product-* attributes may expose a JSON blob
  // instead (e.g. T4S data-product-options: {"id":..., "price":<cents>,...}).
  function cardOptions(card) {
    if (!SELECTORS.itemOptionsAttr) return null;
    try {
      var raw = attr(card, SELECTORS.itemOptionsAttr);
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      return null;
    }
  }

  function itemFromCard(card, ctx, index) {
    if (!card) return {};
    var opts = null;
    var item = { item_id: attr(card, 'data-product-id') };
    if (!item.item_id) {
      opts = cardOptions(card);
      if (opts && opts.id) item.item_id = String(opts.id);
    }
    var name = attr(card, 'data-product-title');
    if (!name && SELECTORS.itemTitleFallback) {
      name = text(card.querySelector(SELECTORS.itemTitleFallback));
    }
    if (name) item.item_name = name;
    var price = attr(card, 'data-product-price');
    if (price) {
      var parsed = parseFloat(price);
      if (!isNaN(parsed)) item.price = parsed;
    } else {
      opts = opts || cardOptions(card);
      if (opts && opts.price !== undefined && !isNaN(parseFloat(opts.price))) {
        // Shopify JSON prices are in the smallest currency unit (cents).
        item.price = parseFloat(opts.price) / 100;
      } else if (SELECTORS.itemPriceFallback) {
        var priceText = text(card.querySelector(SELECTORS.itemPriceFallback));
        var priceNum = parseFloat(priceText.replace(/[^0-9.]/g, ''));
        if (!isNaN(priceNum)) item.price = priceNum;
      }
    }
    if (typeof index === 'number' && index >= 0) item.index = index;
    if (ctx && ctx.id) item.item_list_id = ctx.id;
    if (ctx && ctx.name) item.item_list_name = ctx.name;
    return item;
  }

  function indexOfCard(card) {
    if (!card || !card.parentNode) return -1;
    var cards = card.parentNode.querySelectorAll(SELECTORS.itemCard);
    return Array.prototype.indexOf.call(cards, card);
  }

  function menuTier(el) {
    if (SELECTORS.menuTierClassPattern) {
      var re = new RegExp(SELECTORS.menuTierClassPattern);
      var node = el;
      while (node && node.nodeType === 1) {
        var m = (node.className && typeof node.className === 'string') ? node.className.match(re) : null;
        if (m && m[1] !== undefined) {
          return String(parseInt(m[1], 10) + (SELECTORS.menuTierOffset || 0));
        }
        node = node.parentNode;
      }
      return '';
    }
    if (SELECTORS.menuTierAttr) {
      var levelEl = closest(el, '[' + SELECTORS.menuTierAttr + ']');
      return levelEl ? attr(levelEl, SELECTORS.menuTierAttr) : '';
    }
    return '';
  }

  function desktopTier(link) {
    var containers = SELECTORS.desktopNavTierContainers;
    if (containers && containers.length) {
      for (var i = 0; i < containers.length; i++) {
        if (closest(link, containers[i].selector)) return String(containers[i].tier);
      }
      return '1';
    }
    if (link.classList.contains(SELECTORS.desktopNavDropdownClass)) {
      var isTier2 = SELECTORS.desktopNavTier2Classes.some(function (cls) {
        return link.classList.contains(cls);
      });
      return isTier2 ? '2' : '3';
    }
    return '1';
  }

  // Resolve the Shopify section id for an element; themes that don't render
  // [data-section-id] still wrap sections in .shopify-section with the id in
  // the element id ("shopify-section-<id>").
  function sectionIdOf(el) {
    var sectionEl = closest(el, '[data-section-id]');
    if (sectionEl) return attr(sectionEl, 'data-section-id');
    var wrap = closest(el, '.shopify-section');
    return wrap && wrap.id ? wrap.id.replace(/^shopify-section-/, '') : '';
  }

  function onClick(e) {
    var target = e.target;

    var promoEl = closest(target, PROMO_CLICK_SELECTOR);
    if (promoEl) {
      var marker = closest(promoEl, '[data-ga4-promotion]');
      var promo = promotionData(marker, promoEl);
      var href = attr(promoEl, 'href');
      if (href) promo.link_url = href;
      storePromoContext(promo);
      track('select_promotion', promo);
      return;
    }

    var itemLink = closest(target, SELECTORS.itemLink);
    if (itemLink) {
      var card = closest(itemLink, SELECTORS.itemCard) || closest(itemLink, '[data-product-id]');
      var ctx = listContext(itemLink);
      var item = itemFromCard(card, ctx, indexOfCard(card));
      storeListContext(ctx.id, ctx.name, item.item_id, item.item_name);
      track('select_item', {
        item_list_id: ctx.id,
        item_list_name: ctx.name,
        items: [item]
      });
      // Opt-in, fully event-scoped custom event (e.g. featured collection) so the
      // clicked product + list can be reported without item-scoped dimensions.
      var customEvent = attr(ctx.el, 'data-ga4-event');
      if (customEvent) {
        var customParams = {
          collection_title: attr(ctx.el, 'data-collection-title') || ctx.name,
          collection_id: ctx.id,
          product_title: item.item_name || '',
          product_id: item.item_id || '',
          link_url: attr(itemLink, 'href')
        };
        if (typeof item.price === 'number') customParams.price = item.price;
        if (typeof item.index === 'number') customParams.list_position = item.index;
        track(customEvent, customParams);
      }
      return;
    }

    // Single-product blocks (e.g. featured product) that opt in with
    // [data-ga4-single] fire an event-scoped click event on any link/button.
    var singleWrap = closest(target, '[data-ga4-single]');
    if (singleWrap) {
      var singleEvent = attr(singleWrap, 'data-ga4-event');
      var clickable = closest(target, 'a, button');
      if (singleEvent && clickable) {
        var singleItem = singleWrap.querySelector('[data-ga4-item]');
        var sp = {
          product_title: attr(singleItem, 'data-product-title'),
          product_id: attr(singleItem, 'data-product-id'),
          collection_title: attr(singleWrap, 'data-list-name'),
          link_url: attr(clickable, 'href')
        };
        var singlePrice = parseFloat(attr(singleItem, 'data-product-price'));
        if (!isNaN(singlePrice)) sp.price = singlePrice;
        storeListContext(
          attr(singleWrap, 'data-list-id'),
          attr(singleWrap, 'data-list-name'),
          sp.product_id,
          sp.product_title
        );
        track(singleEvent, sp);
        return;
      }
    }

    var catLink = closest(target, SELECTORS.categoryTile);
    if (catLink) {
      var catMarker = closest(catLink, '[data-ga4-category]');
      var catId = attr(catMarker, 'data-category-id');
      var catName =
        attr(catMarker, 'data-category-name') ||
        text(catLink.querySelector(SELECTORS.categoryTitle)) ||
        text(catLink);
      storeCategoryContext(catId, catName);
      track('select_content', {
        content_type: 'collection',
        item_id: catId,
        category_name: catName,
        link_url: attr(catLink, 'href')
      });
      return;
    }

    var subToggle = closest(target, SELECTORS.mobileSubmenuToggle);
    if (subToggle && closest(subToggle, SELECTORS.mobileNavRoot)) {
      var toggleLabel =
        text(subToggle.querySelector(SELECTORS.mobileSubmenuLabel) || subToggle.querySelector('span')) ||
        text(closest(subToggle, 'a'));
      track('menu_submenu_toggle', {
        menu_name: 'mobile_nav',
        menu_item: toggleLabel,
        menu_tier: menuTier(subToggle)
      });
      return;
    }

    var navLink = closest(target, SELECTORS.mobileNavLink);
    if (navLink) {
      var menuItem = text(navLink.querySelector(SELECTORS.mobileNavLinkLabel)) || text(navLink);
      var menuHref = attr(navLink, 'href');
      var tier = menuTier(navLink);
      storeMenuContext(menuItem, tier, menuHref);
      track('menu_navigate', {
        menu_name: 'mobile_nav',
        menu_item: menuItem,
        link_url: menuHref,
        menu_tier: tier
      });
      return;
    }

    // Desktop header navigation: top bar links, dropdown and megamenu links.
    var desktopLink = closest(target, SELECTORS.desktopNavLink);
    if (desktopLink && !closest(desktopLink, SELECTORS.mobileNavRoot)) {
      var dTier = desktopTier(desktopLink);
      var dItem = text(desktopLink);
      var dHref = attr(desktopLink, 'href');
      storeMenuContext(dItem, dTier, dHref);
      track('menu_navigate', {
        menu_name: 'desktop_nav',
        menu_item: dItem,
        link_url: dHref,
        menu_tier: dTier
      });
      return;
    }

    var sliderCtrl = closest(target, SELECTORS.sliderControl);
    if (sliderCtrl && closest(sliderCtrl, SELECTORS.sliderSection)) {
      var navType = sliderCtrl.classList.contains(SELECTORS.sliderDotClass)
        ? 'dot'
        : sliderCtrl.classList.contains(SELECTORS.sliderPrevClass)
        ? 'previous'
        : 'next';
      track('slider_navigate', {
        location_id: sectionIdOf(sliderCtrl),
        navigation_type: navType
      });
      return;
    }

    // Themes without a DOM menu-open event: track the drawer trigger click.
    if (SELECTORS.mobileMenuOpenTrigger && closest(target, SELECTORS.mobileMenuOpenTrigger)) {
      track('menu_open', { menu_name: 'mobile_nav' });
    }
  }

  function fireViewPromotion(el) {
    track('view_promotion', promotionData(el, el));
  }

  function fireViewItemList(listEl) {
    var ctx = { id: attr(listEl, 'data-list-id'), name: attr(listEl, 'data-list-name') };
    var cards = listEl.querySelectorAll(SELECTORS.itemCard + ', [data-ga4-item][data-product-id]');
    var items = Array.prototype.map.call(cards, function (card, i) {
      return itemFromCard(card, ctx, i);
    });
    if (items.length) {
      track('view_item_list', {
        item_list_id: ctx.id,
        item_list_name: ctx.name,
        items: items
      });
      // Opt-in, event-scoped impression event (e.g. featured product) for easy
      // reporting without item-scoped dimensions.
      var viewEvent = attr(listEl, 'data-ga4-view-event');
      if (viewEvent) {
        var first = items[0] || {};
        var vp = {
          product_title: first.item_name,
          product_id: first.item_id,
          collection_title: ctx.name
        };
        if (typeof first.price === 'number') vp.price = first.price;
        track(viewEvent, vp);
      }
    }
  }

  var promoObserver = null;
  var listObserver = null;

  function setupObservers() {
    if (!('IntersectionObserver' in window)) return;

    promoObserver = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            obs.unobserve(entry.target);
            fireViewPromotion(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    listObserver = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            obs.unobserve(entry.target);
            fireViewItemList(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );
  }

  function scanForTargets() {
    if (promoObserver) {
      var promos = document.querySelectorAll('[data-ga4-promotion]');
      Array.prototype.forEach.call(promos, function (el) {
        if (observed && observed.has(el)) return;
        if (observed) observed.add(el);
        promoObserver.observe(el);
      });
    }
    if (listObserver) {
      var lists = document.querySelectorAll('[data-ga4-list]');
      Array.prototype.forEach.call(lists, function (el) {
        if (observed && observed.has(el)) return;
        if (observed) observed.add(el);
        listObserver.observe(el);
      });
    }
  }

  function watchDynamicContent() {
    if (typeof MutationObserver !== 'function') return;
    var pending = null;
    var mo = new MutationObserver(function () {
      if (pending) return;
      pending = window.setTimeout(function () {
        pending = null;
        scanForTargets();
      }, 400);
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    // Capture phase so we record the interaction before any navigation occurs.
    document.addEventListener('click', onClick, true);
    if (SELECTORS.mobileMenuOpenEvent) {
      document.addEventListener(SELECTORS.mobileMenuOpenEvent, function () {
        track('menu_open', { menu_name: 'mobile_nav' });
      });
    }
    setupObservers();
    scanForTargets();
    watchDynamicContent();
    watchCartAdds();
  }

  // Expose helper for ad-hoc events elsewhere if needed.
  window.GA4Events = { track: track };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
